// useNetworkResilientVideo.js
import { useEffect, useRef } from "react";

/**
 * Makes a <video> element recover automatically after a network drop.
 * Pass the same ref you give to <video ref={videoRef} />.
 */
export function useNetworkResilientVideo(videoRef) {
  const lastTimeRef = useRef(0);
  const wasPlayingRef = useRef(false);
  const needsRecoveryRef = useRef(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const rememberState = () => {
      lastTimeRef.current = video.currentTime;
      wasPlayingRef.current = !video.paused && !video.ended;
    };

    const handleError = () => {
      rememberState();
      needsRecoveryRef.current = true;
    };

    const handleStalledOrWaiting = () => {
      // Only flag recovery if we actually seem offline / stuck
      rememberState();
      needsRecoveryRef.current = true;
    };

    const handleOffline = () => {
      rememberState();
    };

    const handleOnline = () => {
      if (!needsRecoveryRef.current) return;
      recover();
    };

    const recover = () => {
      const time = lastTimeRef.current;
      const shouldPlay = wasPlayingRef.current;

      // Force the browser to re-fetch the source instead of sitting
      // on the old failed request.
      video.load();

      const onLoadedMeta = () => {
        video.currentTime = time;
        if (shouldPlay) {
          video.play().catch(() => {
            // Autoplay might be blocked after a user-agent decides
            // this is a "new" playback; ignore, user can hit play.
          });
        }
        needsRecoveryRef.current = false;
        video.removeEventListener("loadedmetadata", onLoadedMeta);
      };

      video.addEventListener("loadedmetadata", onLoadedMeta);
    };

    video.addEventListener("error", handleError);
    video.addEventListener("stalled", handleStalledOrWaiting);
    video.addEventListener("waiting", handleStalledOrWaiting);
    window.addEventListener("offline", handleOffline);
    window.addEventListener("online", handleOnline);

    return () => {
      video.removeEventListener("error", handleError);
      video.removeEventListener("stalled", handleStalledOrWaiting);
      video.removeEventListener("waiting", handleStalledOrWaiting);
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("online", handleOnline);
    };
  }, [videoRef]);
}